import { Alert, CircularProgress, Snackbar } from "@mui/material"
import ExcelJS from "exceljs"
import React, { useMemo, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import { read, utils } from "xlsx"
import { API_ENDPOINTS } from "../../api/endpoints"
import { useApi } from "../../hooks/useApi"
import { IEgresados, ImportExcelModalProps } from "../../interfaces/IEgresados"
import { CategoriaInvitado, TipoEvento } from "../../interfaces/TipoEvento"
import { getFieldConfig, labelInvitado } from "../../utils/tipoEventoTheme"
import { validateDniFormat } from "../../utils/validation"
import FileUploadButton from "../FileUploadButton/FileUploadButton"
import SpinnerOverlay from "../SpinnerOverlay/SpinnerOverlay"
import "./ImportExcelModal.css"

type RowErrors = Partial<Record<keyof IEgresados, string | undefined>>

interface DniValidationResponse {
  dniExistentes: number[]
}

interface ExcelRow {
  ID?: number
  NOMBRE_DEL_EGRESADO?: string
  NOMBRE_DEL_INVITADO?: string
  DNI?: number
  EGRESADOS?: number
  MAYORES?: number
  MENORES?: number
  BAILE?: number
  TARJETAS_BAILE?: number
  TELEFONO?: string | number
  EMAIL?: string
  MENU_OPCIONAL?: string
  OBSERVACIONES?: string
}

const ImportExcelModal: React.FC<ImportExcelModalProps> = ({
  show,
  handleClose,
  handleImport,
  idEvento,
  tipoEvento,
  categoriaDefault,
}) => {
  const categoria = categoriaDefault ?? CategoriaInvitado.GENERAL
  const cfg = useMemo(
    () => getFieldConfig(tipoEvento, categoria),
    [tipoEvento, categoria],
  )
  const esBaile = categoria === CategoriaInvitado.BAILE
  const esEgresados = tipoEvento === TipoEvento.EGRESADOS
  const validarUnicidadDni = esEgresados && categoria === CategoriaInvitado.CENA
  const labelCategoria = esBaile ? "Baile" : categoria === CategoriaInvitado.CENA ? "Cena" : ""
  const titulo = `Importar ${labelInvitado(tipoEvento)}s${labelCategoria ? ` - ${labelCategoria}` : ""}`

  const [excelData, setExcelData] = useState<IEgresados[]>([])
  const [errors, setErrors] = useState<RowErrors[]>([])
  const fileType = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ]
  const [loadingDnis, setLoadingDnis] = useState<boolean[]>([])
  const [globalLoading] = useState(false)
  const api = useApi()
  const API_URL = API_ENDPOINTS.EGRESADOS
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarErrorOpen, setSnackbarErrorOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState<string>("")
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)

  const handleChange = async (file: File) => {
    setSelectedFile(file)
    if (file && fileType.includes(file.type)) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const workbook = read(e.target?.result)
        const sheet = workbook.SheetNames
        if (sheet.length) {
          const data = utils.sheet_to_json<ExcelRow>(workbook.Sheets[sheet[0]])
          const updatedData: IEgresados[] = data.map((item) => {
            const nombreCompleto = item.NOMBRE_DEL_EGRESADO || item.NOMBRE_DEL_INVITADO || ""
            const [apellido, nombre] = nombreCompleto
              .split(",")
              .map((str: string) => str.trim())
            const dni = item.DNI || 0
            const tarjetasPagadas = esBaile
              ? (item.BAILE || item.TARJETAS_BAILE || 0)
              : ((item.EGRESADOS || 0) + (item.MAYORES || 0))
            return {
              id: item.ID || 0,
              nombre: nombre || "",
              apellido: apellido || "",
              dni,
              clave: "",
              categoria,
              tarjetasPagadas,
              tarjetasPagadasMenores: cfg.tarjetasMayores ? (item.MENORES || 0) : 0,
              telefono: cfg.telefonoMostrar ? String(item.TELEFONO || "") : "",
              email: cfg.emailMostrar ? (item.EMAIL || "") : "",
              menuOpcional: cfg.menuMesa ? String(item.MENU_OPCIONAL || "") : null,
              observaciones: cfg.menuMesa ? String(item.OBSERVACIONES || "") : null,
              eventoId: parseInt(idEvento),
            }
          })
          setExcelData(updatedData)
          validateData(updatedData)
        }
      }
      reader.readAsArrayBuffer(file)
    } else {
      handleError("Por favor, suba un archivo en formato Excel.")
    }
  }
  const handleError = (message: string) => {
    setSnackbarMessage(message)
    setSnackbarErrorOpen(true)
  }

  const validateDNI = async (dni: string, index: number): Promise<void> => {
    if (!validarUnicidadDni) return
    setLoadingDnis((prev) => {
      const newLoading = [...prev]
      newLoading[index] = true
      return newLoading
    })
    try {
      const data = await api.post<DniValidationResponse>(
        `${API_URL}/validarDNI?tipoEvento=${tipoEvento}`,
        [parseInt(dni)],
        { errorMessage: "Error al verificar el DNI" },
      )
      setErrors((prevErrors) => {
        const newErrors = [...prevErrors]
        if (data.dniExistentes.length > 0) {
          newErrors[index] = {
            ...newErrors[index],
            dni: "Este DNI ya existe en la base de datos.",
          }
        } else {
          newErrors[index] = { ...newErrors[index], dni: undefined }
        }
        return newErrors
      })
    } catch {
      setErrors((prevErrors) => {
        const newErrors = [...prevErrors]
        newErrors[index] = { ...newErrors[index], dni: undefined }
        return newErrors
      })
    } finally {
      setLoadingDnis((prev) => {
        const newLoading = [...prev]
        newLoading[index] = false
        return newLoading
      })
    }
  }

  const obtenerDnisDuplicados = (egresados: IEgresados[]): number[] => {
    const dniSet = new Set<number>()
    const dnisDuplicados: number[] = []

    for (const egresado of egresados) {
      if (!egresado.dni) continue
      if (dniSet.has(egresado.dni)) {
        if (!dnisDuplicados.includes(egresado.dni)) {
          dnisDuplicados.push(egresado.dni)
        }
      } else {
        dniSet.add(egresado.dni)
      }
    }

    return dnisDuplicados
  }

  const validateData = (data: IEgresados[]) => {
    const dniRequerido = cfg.dniMostrar && cfg.dniRequerido
    const dniDuplicadosEnExcel = validarUnicidadDni ? obtenerDnisDuplicados(data) : []
    const newErrors: RowErrors[] = []

    if (validarUnicidadDni && dniDuplicadosEnExcel.length > 0) {
      setSnackbarMessage(
        `Los siguientes DNI están duplicados: ${dniDuplicadosEnExcel.join(", ")}`,
      )
      setSnackbarOpen(true)
    } else {
      setSnackbarMessage("")
      setSnackbarOpen(false)
    }

    data.forEach((item, index) => {
      const rowErrors: RowErrors = {}
      const dniStr = item.dni ? item.dni.toString() : ""

      if (!item.nombre) rowErrors.nombre = "El nombre es requerido."
      if (!item.apellido) rowErrors.apellido = "El apellido es requerido."

      if (cfg.dniMostrar) {
        if (validarUnicidadDni && item.dni != null && dniDuplicadosEnExcel.includes(item.dni)) {
          rowErrors.dni = "Este DNI está duplicado."
        } else if (dniRequerido && !validateDniFormat(dniStr)) {
          rowErrors.dni = "El DNI debe tener 8 caracteres."
        } else if (!dniRequerido && dniStr && !validateDniFormat(dniStr)) {
          rowErrors.dni = "El DNI debe tener 8 caracteres."
        }
      }

      newErrors[index] = rowErrors
    })

    setErrors(newErrors)
  }

  const handleInputChange = (
    index: number,
    field: keyof IEgresados,
    value: string | number,
  ) => {
    const newData: IEgresados[] = [...excelData]
    let nextValue: string | number = value
    if (field === "dni") {
      nextValue = parseInt(String(value), 10) || 0
      if (validateDniFormat(nextValue)) {
        validateDNI(String(nextValue), index)
        validateData(newData)
      }
    }

    if (field === "telefono") {
      nextValue = String(value)
    }

    newData[index] = { ...newData[index], [field]: nextValue }
    setExcelData(newData)
    validateData(newData)
  }

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbarOpen(false)
    setSnackbarErrorOpen(false)
  }

  const handleImportData = () => {
    if (
      errors.every(
        (err) =>
          Object.keys(err).length === 0 ||
          Object.values(err).every((value) => value === undefined),
      )
    ) {
      handleImport(excelData)
      handleSnackbarClose()
      handleClose()
    } else {
      setSnackbarMessage("Corrija los errores antes de importar.")
      setSnackbarOpen(true)
    }
  }

  const closeModal = () => {
    setExcelData([])
    setErrors([])
    setSelectedFile(undefined)
    handleClose()
    handleSnackbarClose()
  }

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook()
    const sheetName = esEgresados ? "Egresados" : "Invitados"
    const worksheet = workbook.addWorksheet(sheetName)
    const labelNombre = esEgresados ? "NOMBRE_DEL_EGRESADO" : "NOMBRE_DEL_INVITADO"

    type ColDef = { header: string; key: string; width: number }
    const headers: ColDef[] = [
      { header: labelNombre, key: "nombre", width: 30 },
      ...(cfg.dniMostrar ? [{ header: "DNI", key: "dni", width: 15 }] : []),
      ...(cfg.tarjetasBaile ? [{ header: "BAILE", key: "baile", width: 15 }] : []),
      ...(cfg.tarjetasMayores
        ? [
            { header: "MAYORES", key: "mayores", width: 15 },
            { header: "MENORES", key: "menores", width: 15 },
          ]
        : []),
      ...(cfg.telefonoMostrar
        ? [{ header: "TELEFONO", key: "telefono", width: 20 }]
        : []),
      ...(cfg.emailMostrar
        ? [{ header: "EMAIL", key: "email", width: 30 }]
        : []),
      ...(cfg.menuMesa
        ? [
            { header: "MENU_OPCIONAL", key: "menuOpcional", width: 30 },
            { header: "OBSERVACIONES", key: "observaciones", width: 30 },
          ]
        : []),
    ]

    worksheet.columns = headers
    worksheet.getRow(1).font = { bold: true }

    // Fila ejemplo orientativa
    const exampleRow: Record<string, string | number> = {
      nombre: "Perez, Juan",
      ...(cfg.dniMostrar ? { dni: 12345678 } : {}),
      ...(cfg.tarjetasBaile ? { baile: 1 } : {}),
      ...(cfg.tarjetasMayores ? { mayores: 1, menores: 0 } : {}),
      ...(cfg.telefonoMostrar ? { telefono: "1133334444" } : {}),
      ...(cfg.emailMostrar ? { email: "ejemplo@mail.com" } : {}),
      ...(cfg.menuMesa ? { menuOpcional: "", observaciones: "" } : {}),
    }
    worksheet.addRow(exampleRow)

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: "application/octet-stream" })
    const link = document.createElement("a")
    link.href = window.URL.createObjectURL(blob)
    const sufijo = labelCategoria ? `_${labelCategoria}` : ""
    link.download = `Plantilla_${sheetName}${sufijo}.xlsx`
    link.click()
  }

  return (
    <>
      <Modal show={show} onHide={closeModal} size="xl">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <Modal.Title>{titulo}</Modal.Title>
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleDownloadTemplate}
              title="Descargar plantilla Excel"
            >
              <i className="bi bi-download me-2"></i>
              Descargar plantilla
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {globalLoading && <SpinnerOverlay />}
          <div style={{ display: "flex", alignItems: "center" }}>
            <FileUploadButton
              onFileChange={handleChange}
              onError={handleError}
            />
            {selectedFile && (
              <p style={{ marginLeft: "10px", marginTop: "10px" }}>
                Archivo seleccionado: {selectedFile.name}
              </p>
            )}
          </div>
          <div className="data-table-container">
            <table className="table">
              <thead>
                <tr className="trhead">
                  <th>Nº</th>
                  <th>Apellido</th>
                  <th>Nombre</th>
                  {cfg.dniMostrar && <th>DNI{!cfg.dniRequerido && " (opc.)"}</th>}
                  <th>{cfg.tarjetasBaile ? "Tarjetas Baile" : "Tarjetas Pagadas"}</th>
                  {cfg.tarjetasMayores && <th>Tarjetas Pagadas Menores</th>}
                  {esEgresados && <th>Contraseña (auto)</th>}
                  {cfg.emailMostrar && <th>Email</th>}
                  {cfg.telefonoMostrar && <th>Teléfono</th>}
                  {cfg.menuMesa && <th>Menú Opcional</th>}
                  {cfg.menuMesa && <th>Observaciones</th>}
                </tr>
              </thead>
              <tbody>
                {excelData.length ? (
                  excelData.map((info, index) => {
                    const hasErrors =
                      Object.keys(errors[index] || {}).length > 0

                    return (
                      <tr key={index} className={hasErrors ? "error-row" : ""}>
                        <td className={`input-cell`}>{index + 1}</td>
                        <td
                          className={`input-cell ${
                            errors[index]?.apellido ? "error" : ""
                          }`}
                        >
                          <input
                            type="text"
                            value={info.apellido}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "apellido",
                                e.target.value,
                              )
                            }
                            className={`form-control ${
                              errors[index]?.apellido ? "error-field" : ""
                            }`}
                          />
                          {errors[index]?.apellido && (
                            <div className="error-message">
                              {errors[index].apellido}
                            </div>
                          )}
                        </td>
                        <td
                          className={`input-cell ${
                            errors[index]?.nombre ? "error" : ""
                          }`}
                        >
                          <input
                            type="text"
                            value={info.nombre}
                            onChange={(e) =>
                              handleInputChange(index, "nombre", e.target.value)
                            }
                            className={`form-control ${
                              errors[index]?.nombre ? "error-field" : ""
                            }`}
                          />
                          {errors[index]?.nombre && (
                            <div className="error-message">
                              {errors[index].nombre}
                            </div>
                          )}
                        </td>
                        {cfg.dniMostrar && (
                          <td
                            className={`input-cell ${
                              errors[index]?.dni ? "error" : ""
                            }`}
                          >
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <input
                                type="text"
                                value={info.dni ?? ""}
                                onChange={(e) =>
                                  handleInputChange(index, "dni", e.target.value)
                                }
                                className={`form-control ${
                                  errors[index]?.dni ? "error-field" : ""
                                }`}
                                style={{ flex: 1 }}
                              />
                              {loadingDnis[index] && (
                                <CircularProgress
                                  size={24}
                                  style={{ marginLeft: "10px" }}
                                />
                              )}
                            </div>
                            {errors[index]?.dni && (
                              <div className="error-message">
                                {errors[index].dni}
                              </div>
                            )}
                          </td>
                        )}
                        <td
                          className={`input-cell ${
                            errors[index]?.tarjetasPagadas ? "error" : ""
                          }`}
                        >
                          <input
                            type="text"
                            value={info.tarjetasPagadas}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "tarjetasPagadas",
                                e.target.value,
                              )
                            }
                            className={`form-control ${
                              errors[index]?.tarjetasPagadas
                                ? "error-field"
                                : ""
                            }`}
                          />
                          {errors[index]?.tarjetasPagadas && (
                            <div className="error-message">
                              {errors[index].tarjetasPagadas}
                            </div>
                          )}
                        </td>
                        {cfg.tarjetasMayores && (
                          <td
                            className={`input-cell ${
                              errors[index]?.tarjetasPagadasMenores ? "error" : ""
                            }`}
                          >
                            <input
                              type="text"
                              value={info.tarjetasPagadasMenores}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "tarjetasPagadasMenores",
                                  e.target.value,
                                )
                              }
                              className={`form-control ${
                                errors[index]?.tarjetasPagadasMenores
                                  ? "error-field"
                                  : ""
                              }`}
                            />
                            {errors[index]?.tarjetasPagadasMenores && (
                              <div className="error-message">
                                {errors[index].tarjetasPagadasMenores}
                              </div>
                            )}
                          </td>
                        )}
                        {esEgresados && (
                          <td className="input-cell">
                            <span style={{ color: "#888", fontStyle: "italic", fontSize: "0.85em" }}>
                              Auto-generada
                            </span>
                          </td>
                        )}
                        {cfg.emailMostrar && (
                          <td
                            className={`input-cell ${
                              errors[index]?.email ? "error" : ""
                            }`}
                          >
                            <input
                              type="email"
                              value={info.email}
                              onChange={(e) =>
                                handleInputChange(index, "email", e.target.value)
                              }
                              className={`form-control ${
                                errors[index]?.email ? "error-field" : ""
                              }`}
                            />
                            {errors[index]?.email && (
                              <div className="error-message">
                                {errors[index].email}
                              </div>
                            )}
                          </td>
                        )}
                        {cfg.telefonoMostrar && (
                          <td
                            className={`input-cell ${
                              errors[index]?.telefono ? "error" : ""
                            }`}
                          >
                            <input
                              type="text"
                              value={info.telefono}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "telefono",
                                  e.target.value,
                                )
                              }
                              className={`form-control ${
                                errors[index]?.telefono ? "error-field" : ""
                              }`}
                            />
                            {errors[index]?.telefono && (
                              <div className="error-message">
                                {errors[index].telefono}
                              </div>
                            )}
                          </td>
                        )}
                        {cfg.menuMesa && (
                          <td
                            className={`input-cell ${
                              errors[index]?.menuOpcional ? "error" : ""
                            }`}
                          >
                            <input
                              type="text"
                              value={info.menuOpcional ?? ""}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "menuOpcional",
                                  e.target.value,
                                )
                              }
                              className={`form-control ${
                                errors[index]?.menuOpcional ? "error-field" : ""
                              }`}
                            />
                          </td>
                        )}
                        {cfg.menuMesa && (
                          <td
                            className={`input-cell ${
                              errors[index]?.observaciones ? "error" : ""
                            }`}
                          >
                            <input
                              type="text"
                              value={info.observaciones ?? ""}
                              onChange={(e) =>
                                handleInputChange(
                                  index,
                                  "observaciones",
                                  e.target.value,
                                )
                              }
                              className={`form-control ${
                                errors[index]?.observaciones ? "error-field" : ""
                              }`}
                            />
                          </td>
                        )}
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={11}>No hay datos disponibles</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Snackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleSnackbarClose} severity="warning">
              {snackbarMessage}
            </Alert>
          </Snackbar>
          <Snackbar
            open={snackbarErrorOpen}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleSnackbarClose} severity="error">
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={handleImportData}
            disabled={!selectedFile}
          >
            Importar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ImportExcelModal
